import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

export default function useContaMesa({ authAxios, table, onBack }) {
  const [orders, setOrders] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [showMultiPayModal, setShowMultiPayModal] = useState(false);
  const [splitParts, setSplitParts] = useState(2);
  const [splitType, setSplitType] = useState("equal");
  const [processing, setProcessing] = useState(false);
  const [cashReceived, setCashReceived] = useState("");

  // Multi-payment state
  const [multiPayments, setMultiPayments] = useState([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState("cash");
  const [newPaymentAmount, setNewPaymentAmount] = useState("");

  // Payment success state — requires explicit ENCERRAR MESA
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paidTotal, setPaidTotal] = useState(0);
  const [paidChange, setPaidChange] = useState(0);

  const fetchTableOrders = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await authAxios.get(`/pos/table-orders/${table.number || table.id}`);
      setOrders(res.data.orders || []);
    } catch {
      try {
        const res = await authAxios.get(`/orders?table_number=${table.number}`);
        const tableOrders = (res.data || []).filter(
          (o) => o.table_number === table.number && o.payment_status === "pending"
        );
        setOrders(tableOrders);
      } catch {
        setOrders([]);
      }
    }
    // Fetch account data to get guest count
    try {
      const accountRes = await authAxios.get(`/tables/${table.id}/account`);
      setAccount(accountRes.data);
    } catch {
      setAccount(null);
    }
    if (!silent) setLoading(false);
  };

  const fetchRef = useRef(fetchTableOrders);
  fetchRef.current = fetchTableOrders;

  useEffect(() => {
    fetchRef.current();
    const interval = setInterval(() => fetchRef.current({ silent: true }), 4000);
    return () => clearInterval(interval);
  }, [table.id]);

  const total = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const allItems = orders.flatMap((o) => o.items || []);
  const change = cashReceived ? Math.max(0, parseFloat(cashReceived) - total) : 0;

  // Multi-payment calculations
  const totalPaid = multiPayments.reduce((sum, p) => sum + p.amount, 0);
  const remaining = Math.max(0, total - totalPaid);

  const handlePayment = async (method, receivedAmount = null) => {
    setProcessing(true);
    try {
      for (const order of orders) {
        await authAxios.patch(`/orders/${order.id}`, {
          payment_status: "paid",
          payment_method: method,
          status: "delivered",
          cash_received: receivedAmount,
          change_given: receivedAmount ? receivedAmount - total : 0,
        });
      }
      const changeAmt = receivedAmount ? Math.max(0, receivedAmount - total) : 0;
      setPaidTotal(total);
      setPaidChange(changeAmt);
      setShowPaymentModal(false);
      setShowCashModal(false);
      setPaymentSuccess(true);
      toast.success(`Pagamento de €${total.toFixed(2)} registrado!`);
    } catch {
      toast.error("Erro ao registrar pagamento");
    }
    setProcessing(false);
  };

  const handleMultiPaymentConfirm = async () => {
    if (remaining > 0.01) {
      toast.error(`Falta pagar €${remaining.toFixed(2)}`);
      return;
    }
    setProcessing(true);
    try {
      for (const order of orders) {
        await authAxios.patch(`/orders/${order.id}`, {
          payment_status: "paid",
          payment_method: "multi",
          payment_details: multiPayments,
          status: "delivered",
        });
      }
      const methodsUsed = [...new Set(multiPayments.map((p) => p.method))].join(" + ");
      setPaidTotal(total);
      setPaidChange(0);
      setShowMultiPayModal(false);
      setPaymentSuccess(true);
      toast.success(`Pagamento misto (${methodsUsed}) registrado!`);
    } catch {
      toast.error("Erro ao registrar pagamento");
    }
    setProcessing(false);
  };

  const addMultiPayment = () => {
    const amount = parseFloat(newPaymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (amount > remaining + 0.01) {
      toast.error(`Valor máximo: €${remaining.toFixed(2)}`);
      return;
    }
    setMultiPayments([...multiPayments, { method: newPaymentMethod, amount }]);
    setNewPaymentAmount("");
  };

  const removeMultiPayment = (idx) => {
    setMultiPayments(multiPayments.filter((_, i) => i !== idx));
  };

  const handleSplitBill = () => {
    if (splitType === "equal") {
      const perPerson = (total / splitParts).toFixed(2);
      toast.success(`Conta dividida: €${perPerson} por pessoa`);
      const payments = Array(splitParts)
        .fill(null)
        .map((_, i) => ({
          method: "pending",
          amount: parseFloat((total / splitParts).toFixed(2)),
          label: `Pessoa ${i + 1}`,
        }));
      setMultiPayments(payments);
      setShowSplitModal(false);
      setShowMultiPayModal(true);
    }
  };

  const handleCloseTable = async () => {
    try {
      await authAxios.post(`/tables/${table.id}/close`);
      toast.success(
        `Mesa ${table.name || table.number} encerrada — disponível para novos clientes`
      );
      onBack();
    } catch {
      toast.error("Erro ao encerrar mesa");
    }
  };

  const handleUpdateItemQuantity = async (item, delta) => {
    const newQty = item.quantity + delta;
    if (newQty <= 0) {
      handleRemoveItem(item);
      return;
    }
    try {
      const order = orders.find((o) => o.items?.some((i) => i.product_id === item.product_id));
      if (!order) return;
      const updatedItems = order.items.map((i) =>
        i.product_id === item.product_id ? { ...i, quantity: newQty } : i
      );
      const newTotal = updatedItems.reduce((sum, i) => sum + i.quantity * i.unit_price, 0);
      await authAxios.patch(`/orders/${order.id}`, { items: updatedItems, total: newTotal });
      toast.success("Quantidade atualizada");
      fetchTableOrders();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const handleRemoveItem = async (item) => {
    try {
      let removed = false;
      const order = orders.find((o) =>
        o.items?.some((i) => {
          if (!removed && i.product_id === item.product_id &&
              JSON.stringify(i.selected_variations) === JSON.stringify(item.selected_variations)) {
            removed = true;
            return true;
          }
          return false;
        })
      );
      if (!order) return;
      let foundFirst = false;
      const updatedItems = order.items.filter((i) => {
        if (!foundFirst && i.product_id === item.product_id &&
            JSON.stringify(i.selected_variations) === JSON.stringify(item.selected_variations)) {
          foundFirst = true;
          return false;
        }
        return true;
      });
      if (updatedItems.length === 0) {
        // Cancel instead of delete — preserves history for anti-theft tracking
        await authAxios.patch(`/orders/${order.id}/status`, null, { params: { status: "cancelled" } });
        toast.success("Pedido cancelado e guardado no histórico");
      } else {
        const newTotal = updatedItems.reduce((sum, i) => sum + i.quantity * i.unit_price, 0);
        await authAxios.patch(`/orders/${order.id}`, { items: updatedItems, total: newTotal });
        toast.success("Item removido");
      }
      fetchTableOrders();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  return {
    // data
    orders,
    account,
    loading,
    allItems,
    total,
    change,
    // multi-payment
    multiPayments,
    newPaymentMethod,
    setNewPaymentMethod,
    newPaymentAmount,
    setNewPaymentAmount,
    totalPaid,
    remaining,
    // split
    splitParts,
    setSplitParts,
    splitType,
    setSplitType,
    // cash
    cashReceived,
    setCashReceived,
    // processing / success
    processing,
    paymentSuccess,
    paidTotal,
    paidChange,
    // modal visibility
    showPaymentModal,
    setShowPaymentModal,
    showSplitModal,
    setShowSplitModal,
    showCashModal,
    setShowCashModal,
    showMultiPayModal,
    setShowMultiPayModal,
    // handlers
    handlePayment,
    handleMultiPaymentConfirm,
    addMultiPayment,
    removeMultiPayment,
    handleSplitBill,
    handleCloseTable,
    handleUpdateItemQuantity,
    handleRemoveItem,
  };
}
